import React, { useState, useContext, useEffect, Fragment } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import useNRMutation from '../../../graphql/UseNRMutation';
import NRButtonProgress from '../../NRButtonProgress/NRButtonProgress';
import { useSnackbar } from 'notistack';
import Divider from '@mui/material/Divider';
import { GridContext } from '../../NRReactGrid/NRReactGridContext';
import { useParams } from 'react-router-dom';
import { FiltersContext } from '../../../pages/NRReportTabs/NRReportFilterContextWrapper';
import { RemediationContext } from '../../NRRemediation/NRRemediationContext';
import { GET_SECRET, QL_SECRETS, REMEDIATE_SECRETS } from '../../../graphql/queries/secrets';
import { REMEDIATION_CONSTANTS } from '../../NRRemediation/NRRemediation.constants';
import StepperIndicator from '../../NRRemediation/NRRemediationContent/StepperIndicator';
import NRSecretRemediationStepOne from './NRSecretRemediationStepOne';
import NRSecretRemediationStepTwo from './NRSecretRemediationStepTwo';

const steps = ['Status', 'Details'];

function NRSecretRemediationContent({
  inDrawer = false,
  secrets,
  onSubmitExtraActions = () => {},
  onResponseExtraActions = () => {},
  onCancelExtraActions = () => {}
}) {
  const { setOpenRemediationModal, lastQuerySecretsVariables } = useContext(RemediationContext);
  const { setDrawerContent, drawerContent } = useContext(FiltersContext);
  const { componentId } = useParams();
  const { setShouldClearSelection } = useContext(GridContext);
  const { enqueueSnackbar } = useSnackbar();
  const [remediateSecrets, result, loading] = useNRMutation(REMEDIATE_SECRETS, 'remediateSecrets', null, null, {
    refetchQueries: [{ query: QL_SECRETS, variables: { ...lastQuerySecretsVariables }, context: { version: 'v3' } }],
    awaitRefetchQueries: true,
    onCompleted: (result, refetch) => {
      if (inDrawer) {
        const newDrawerContent = [...drawerContent];
        setDrawerContent(newDrawerContent);
      }
    }
  });

  const emptyForm = {
    status: secrets?.length === 1 ? secrets[0].currentRemediation?.status : REMEDIATION_CONSTANTS.STATUS.VALUES.UNREVIEWED,
    description: secrets?.length === 1 ? secrets[0].currentRemediation?.description : '',
    secretIds: secrets?.map(secret => secret.issueId)
  };
  const methods = useForm({
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: emptyForm
  });
  const { handleSubmit, watch, setValue } = methods;
  const form = watch();

  const onSubmitHandler = values => {
    onSubmitExtraActions(form);
    remediateSecrets({
      context: { version: 'v3' },
      variables: {
        args: {
          assetId: componentId,
          description: values.description,
          secretIds: secrets?.map(secret => secret.id),
          status: values.status
        }
      }
    });
  };

  useEffect(() => {
    if (result?.length) {
      enqueueSnackbar(`Remediated ${secrets?.length} secret${secrets?.length > 1 ? 's' : ''} successfully`, { variant: 'success' });
      setOpenRemediationModal(false);
      onResponseExtraActions(form);
      setShouldClearSelection(true);
    }
  }, [result]);

  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleCancel = () => {
    onCancelExtraActions();
  };

  return (
    <Box {...(inDrawer && { height: '100%' })}>
      <FormProvider {...methods}>
        <form {...(inDrawer && { style: { height: '100%' } })} noValidate autoComplete="off" onSubmit={handleSubmit(onSubmitHandler)}>
          <Box
            width={'100%'}
            sx={{
              ...(inDrawer && {
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              })
            }}
          >
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel icon={<StepperIndicator number={index} completed={activeStep > index} active={activeStep === index} />}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <Fragment>
              {activeStep === 0 && <NRSecretRemediationStepOne />}
              {activeStep === 1 && <NRSecretRemediationStepTwo />}
              {inDrawer && <Divider sx={{ marginTop: 'auto', marginLeft: '-24px', marginRight: '-24px' }} />}
              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                <Button variant={'outlined'} color={'secondary'} onClick={() => handleCancel()} sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />
                <Button variant={'outlined'} color={'secondary'} disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
                  Back
                </Button>
                {activeStep !== steps.length - 1 && (
                  <Button onClick={handleNext} variant={'contained'} color={'secondary'}>
                    Next
                  </Button>
                )}
                {activeStep === steps.length - 1 && (
                  <NRButtonProgress variant="contained" color="secondary" type={'submit'} disabled={loading} isLoading={loading} label={'Finish'} />
                )}
              </Box>
            </Fragment>
          </Box>
        </form>
      </FormProvider>
    </Box>
  );
}

export default NRSecretRemediationContent;
